/**
 * Mitarbeiterliste: Suche, Reihenfolge, Filter und IBAN-Schreibweise.
 *
 * Oben steht, wer Trinkgeld bekommt und keine IBAN hat. Das ist der Fall, der
 * eine Auszahlung aufhält, und genau so sieht ein neuer Name aus dem
 * Stundenimport aus.
 */

export type IbanStatus = "vorhanden" | "fehlt_wartet" | "fehlt";

export interface EmployeeDto {
  id: string;
  personalNr: number;
  timeClockId: number | null;
  firstName: string;
  lastName: string;
  email: string | null;
  ibanLast4: string | null;
  /** Name, mit dem überwiesen wird. `null` ohne Recht auf Bankdaten. */
  accountHolder: string | null;
  hasIban: boolean;
  ibanStatus: IbanStatus;
  /** Noch nicht ausgezahltes Trinkgeld, als String. */
  offenesTrinkgeld: string | null;
  active: boolean;
  createdAt: string;
}

export type EmployeeFilter = "wartet" | "ohne_iban" | "aktiv" | "ausgeschieden";

export const FILTERS: { value: EmployeeFilter; label: string }[] = [
  { value: "wartet", label: "IBAN fehlt, Trinkgeld wartet" },
  { value: "ohne_iban", label: "ohne IBAN" },
  { value: "aktiv", label: "alle aktiven" },
  { value: "ausgeschieden", label: "ausgeschieden" },
];

export function fullName(e: { firstName: string; lastName: string }): string {
  return `${e.firstName} ${e.lastName}`.trim();
}

/** „DE89370400440532013000" → „DE89 3704 0044 0532 0130 00", wie auf der Bankkarte. */
export function formatIban(iban: string): string {
  return iban
    .replace(/\s+/g, "")
    .toUpperCase()
    .replace(/(.{4})(?=.)/g, "$1 ");
}

/** Sucht in Name, Personalnummer und Stempeluhr-ID; Groß- und Kleinschreibung egal. */
export function matchesSearch(e: EmployeeDto, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    fullName(e).toLowerCase().includes(q) ||
    `${e.lastName} ${e.firstName}`.toLowerCase().includes(q) ||
    String(e.personalNr) === q ||
    (e.timeClockId !== null && String(e.timeClockId) === q)
  );
}

export function matchesFilter(e: EmployeeDto, filter: EmployeeFilter): boolean {
  switch (filter) {
    case "wartet":
      return e.ibanStatus === "fehlt_wartet";
    case "ohne_iban":
      return e.active && !e.hasIban;
    case "aktiv":
      return e.active;
    case "ausgeschieden":
      return !e.active;
  }
}

/**
 * Wo die Liste beim Öffnen steht: auf den Wartenden, wenn es welche gibt —
 * sonst auf allen Aktiven. Ein leerer Filter als Startbild sähe aus wie ein Fehler.
 */
export function defaultFilter(list: readonly EmployeeDto[]): EmployeeFilter {
  return list.some((e) => e.ibanStatus === "fehlt_wartet") ? "wartet" : "aktiv";
}

/** Wartende zuerst, dann nach Nachname, Vorname. */
export function sortEmployees(list: readonly EmployeeDto[]): EmployeeDto[] {
  return [...list].sort((a, b) => {
    const wa = a.ibanStatus === "fehlt_wartet" ? 0 : 1;
    const wb = b.ibanStatus === "fehlt_wartet" ? 0 : 1;
    return (
      wa - wb ||
      a.lastName.localeCompare(b.lastName, "de") ||
      a.firstName.localeCompare(b.firstName, "de") ||
      a.personalNr - b.personalNr
    );
  });
}
